import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Sparkle, Quotes, ArrowUpRight, X } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";

/** Natural-language search over every meeting transcript. Backend does hybrid
 * retrieval (Mongo regex shortlist → Claude synthesizes an answer with note_id +
 * jump-timestamp citations). Click a citation to open the source note.
 *
 * Dismissal (Iter 85.98): the result panel dismisses on
 *   · click outside the widget (respected only while the panel is open)
 *   · Escape key while focused inside the widget
 *   · X (close) affordance on the answer card itself
 *   · clearing the query input (empty string collapses the panel)
 * The panel stays open while citations are being clicked because the
 * click-outside handler explicitly checks the widget root.
 */
export default function MeetingRecallSearch({ onOpenNote }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const rootRef = useRef(null);

  const search = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (query.length < 2) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/notes/search-meetings`, { params: { q: query } });
      setResult(data);
    } catch (err) {
      setResult({ answer: err?.response?.data?.detail || "Search failed", citations: [] });
    } finally { setLoading(false); }
  };

  // Dismiss the result panel on outside-click / Escape. Attached only
  // while a result is showing so the widget is a no-op the rest of the
  // time.
  useEffect(() => {
    if (!result) return;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setResult(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape" && rootRef.current?.contains(document.activeElement)) setResult(null);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [result]);

  // Clearing the input also clears any stale result so the widget
  // returns to a clean state without needing to click away.
  const onQueryChange = (e) => {
    const next = e.target.value;
    setQ(next);
    if (!next.trim() && result) setResult(null);
  };

  const dismiss = () => setResult(null);

  const fmtTs = (s) => {
    if (s == null) return "";
    const n = Math.floor(s);
    return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
  };

  return (
    <div ref={rootRef} className="mb-4 border border-border bg-card p-3" data-testid="meeting-recall-search">
      <form onSubmit={search} className="flex items-center gap-2">
        <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
        <Input
          data-testid="meeting-recall-input"
          value={q}
          onChange={onQueryChange}
          placeholder="Ask across all meetings — e.g. 'what did we decide with Acme about pricing?'"
          className="rounded-none bg-secondary/40 border-border flex-1"
        />
        <button
          type="submit"
          disabled={loading || q.trim().length < 2}
          data-testid="meeting-recall-submit"
          className="inline-flex items-center gap-1.5 border border-primary bg-primary text-primary-foreground px-3 py-2 text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          <Sparkle size={12} weight="fill" /> {loading ? "Searching…" : "Recall"}
        </button>
      </form>

      {result && (
        <div className="mt-3 border-l-2 border-primary/60 bg-primary/5 p-3 relative" data-testid="meeting-recall-result">
          <button
            type="button"
            onClick={dismiss}
            data-testid="meeting-recall-dismiss"
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1"
            aria-label="Dismiss recall result"
            title="Dismiss (Esc)"
          >
            <X size={12} weight="bold" />
          </button>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-1 flex items-center gap-2 pr-6">
            <Sparkle size={10} weight="fill" /> YadidAi · answer
            {result.candidate_count > 0 && (
              <span className="text-muted-foreground">· {result.candidate_count} meeting{result.candidate_count === 1 ? "" : "s"} matched</span>
            )}
          </div>
          <div className="mb-3" data-testid="meeting-recall-answer">
            <MarkdownDocument>{result.answer}</MarkdownDocument>
          </div>
          {(result.citations || []).length > 0 && (
            <div className="space-y-2">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Citations</div>
              {result.citations.map((c, i) => (
                <button
                  key={c.note_id + i}
                  onClick={() => onOpenNote?.(c.note_id)}
                  data-testid={`meeting-recall-citation-${i}`}
                  className="w-full text-left border border-border p-2 hover:border-primary/60 hover:bg-primary/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow flex items-center gap-1">
                        <Quotes size={9} weight="fill" /> {c.note_title} {c.start_seconds != null && <span className="text-muted-foreground">· @{fmtTs(c.start_seconds)}</span>}
                      </div>
                      <div className="text-xs italic mt-0.5 text-muted-foreground">"{c.quote}"</div>
                    </div>
                    <ArrowUpRight size={12} className="text-muted-foreground shrink-0 mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
